/**
 * ThreadList component - displays the list of messaging threads
 * Supports search, sorting, and displays thread previews
 */
'use client';

import React, { useRef, useState, useEffect } from 'react';
import {
  Search, X, ChevronDown, Users, UsersRound, Check, ArrowUpDown, MessageSquare, type LucideIcon
} from 'lucide-react';
import type { ClientThread, TypingUser } from './types';
import { isThreadTypeJob, isThreadTypeSchedule } from '@/lib/messageSoundPreferences';
import { isUserOnline, getOnlineIndicatorClasses } from '@/lib/onlineStatus';
import { formatDistanceToNow } from 'date-fns';
import { useTheme } from '@/contexts/ThemeContext';
import useModuleColor from '@/hooks/useModuleColor';
import { cn } from '@/lib/utils';
import BrandedLoadingSpinner from '@/components/shared/BrandedLoadingSpinner';

interface ThreadListProps {
  threads: ClientThread[];
  selectedThread: ClientThread | null;
  onSelectThread: (thread: ClientThread) => void;
  loading?: boolean;
  isDark?: boolean; // Deprecated - uses useTheme internally

  // Search & sort
  searchQuery: string;
  onSearchChange: (query: string) => void;
  sortBy: 'newest' | 'oldest' | 'name' | 'unread';
  onSortChange: (sort: 'newest' | 'oldest' | 'name' | 'unread') => void;
  isSearching?: boolean;
  onClearSearch?: () => void;

  // Multi-client support
  totalClientsInThreads?: number;

  // Typing indicators
  typingUsers?: Map<string, TypingUser[]>;

  // Current user
  currentUserId?: number;

  // Client logo for job/schedule/client threads
  clientLogo?: string | null;

  // Empty state
  emptyStateMessage?: string;
  emptyStateIcon?: React.ReactNode;

  /** All / Direct / Groups / Teams — inbox-style dropdown when set */
  categoryFilter?: {
    value: string;
    onChange: (key: string) => void;
    options: Array<{ key: string; label: string; icon: LucideIcon; count?: number }>;
  };

  /** Optional tone for embedded surfaces that should visually match the email module. */
  surfaceVariant?: 'default' | 'email';

  /** Optional toolbar content rendered below the search/sort controls. */
  headerToolbar?: React.ReactNode;

  /** When true, the built-in filter/search/sort toolbar is hidden (e.g. the
   * parent renders a shared ModalFilterBar in the modal header instead). */
  hideControls?: boolean;
}

type ThreadMemberPresence = {
  show_when_online?: boolean;
  last_activity_at?: string;
  has_active_session?: boolean;
};

// Helper to check if a member is online (respects show_when_online preference)
function isMemberOnline(member: ThreadMemberPresence | null | undefined): boolean {
  if (!member) return false;

  // If member has opted out of showing online status, don't show as online
  if (member.show_when_online === false) return false;

  // Check last_activity_at - consider online if active in last 5 minutes
  if (member.last_activity_at) {
    const lastActive = new Date(member.last_activity_at).getTime();
    const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;
    return lastActive > fiveMinutesAgo;
  }

  return false;
}

// Helper to check if we should show online indicator for a member
function shouldShowMemberOnline(member: ThreadMemberPresence | null | undefined): boolean {
  if (!member) return false;
  // Respect the member's preference
  return member.show_when_online !== false;
}

// Helper to get last seen text (matches OperationsCallModal format)
function getMemberLastSeen(member: ThreadMemberPresence | null | undefined): string | null {
  if (!member) return null;

  // If member has disabled show_when_online, don't show their last seen
  if (member.show_when_online === false) return null;

  // If user is signed out (no active session), show as offline
  if (member.has_active_session === false) return 'Offline';

  // If no last_activity_at but has active session, show as available
  if (!member.last_activity_at) {
    return member.has_active_session ? 'Available' : null;
  }

  try {
    const lastActive = new Date(member.last_activity_at);
    const now = new Date();
    const diffMinutes = Math.floor((now.getTime() - lastActive.getTime()) / (1000 * 60));

    // Within last 5 mins = Online
    if (diffMinutes <= 5) return 'Online';
    // Within last hour = Xm ago
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    // Within last 24 hours = Xh ago
    if (diffMinutes < 1440) return `${Math.floor(diffMinutes / 60)}h ago`;

    // More than 24 hours ago - show date/time in SAST format
    const sastTime = lastActive.toLocaleString('en-ZA', {
      timeZone: 'Africa/Johannesburg',
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
    return sastTime;
  } catch {
    return null;
  }
}

// Format thread timestamp (WhatsApp-style)
function formatThreadTime(dateStr: string): string {
  try {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const daysDiff = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (daysDiff === 0) {
      // Today - show time
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (daysDiff === 1) {
      return 'Yesterday';
    } else if (daysDiff < 7) {
      // Show day name
      return date.toLocaleDateString([], { weekday: 'short' });
    } else {
      // Show date
      return date.toLocaleDateString([], { day: 'numeric', month: 'short' });
    }
  } catch {
    return '';
  }
}

export default function ThreadList({
  threads,
  selectedThread,
  onSelectThread,
  loading = false,
  isDark: isDarkProp, // Deprecated prop
  searchQuery,
  onSearchChange,
  sortBy,
  onSortChange,
  isSearching = false,
  onClearSearch,
  totalClientsInThreads = 1,
  typingUsers,
  currentUserId,
  clientLogo = null,
  emptyStateMessage = 'No conversations yet',
  emptyStateIcon,
  categoryFilter,
  surfaceVariant = 'default',
  headerToolbar,
  hideControls = false,
}: ThreadListProps) {
  // Use theme context for proper dark mode detection
  const { currentTheme } = useTheme();
  const isDark = currentTheme?.mode === 'dark';
  const { moduleColor } = useModuleColor('total-connect');

  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const sortDropdownRef = useRef<HTMLDivElement>(null);
  const categoryDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showSortDropdown && !showCategoryDropdown) return;

    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;
      if (
        showCategoryDropdown &&
        categoryDropdownRef.current &&
        !categoryDropdownRef.current.contains(target)
      ) {
        setShowCategoryDropdown(false);
      }
      if (
        showSortDropdown &&
        sortDropdownRef.current &&
        !sortDropdownRef.current.contains(target)
      ) {
        setShowSortDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showSortDropdown, showCategoryDropdown]);

  // Get typing users for a thread
  const getTypingForThread = (threadId: string): TypingUser[] => {
    return typingUsers?.get(threadId) || [];
  };

  const useEmailSurface = surfaceVariant === 'email';

  return (
    <div className="flex flex-col h-full">
      {/* Inbox-style filter + search + sort (aligned with Total Connect email) */}
      {!hideControls && (
      <div
        className={cn(
          useEmailSurface ? 'border-b px-2 py-3' : 'border-b p-2',
          isDark
            ? useEmailSurface
              ? 'border-gray-700 bg-gray-900'
              : 'border-gray-700 bg-gray-800'
            : useEmailSurface
              ? 'border-gray-200 bg-white'
              : 'border-gray-200 bg-gray-50',
        )}
      >
        <div className="flex min-w-0 items-center gap-2">
          {categoryFilter ? (
            <div
              ref={categoryDropdownRef}
              className="relative w-[min(38%,10.5rem)] flex-shrink-0 sm:w-44"
            >
              {(() => {
                const active = categoryFilter.options.find((o) => o.key === categoryFilter.value);
                const IconCmp = active?.icon ?? MessageSquare;
                return (
                  <>
                    <button
                      type="button"
                      onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                      className={cn(
                        'flex w-full min-w-0 items-center gap-1.5 rounded-lg border px-2 py-1.5',
                        useEmailSurface
                          ? isDark
                            ? 'border-gray-600 bg-gray-900 text-gray-100'
                            : 'border-gray-200 bg-white text-gray-900'
                          : isDark
                            ? 'border-gray-600 bg-gray-700 text-gray-100'
                            : 'border-gray-200 bg-white text-gray-900',
                      )}
                    >
                      <IconCmp className="h-4 w-4 flex-shrink-0 text-blue-600 dark:text-blue-400" />
                      <span className="min-w-0 flex-1 truncate text-left text-xs font-medium sm:text-sm">
                        {active?.label ?? 'All'}
                      </span>
                      {active && active.count !== undefined && active.count > 0 && (
                        <span className="flex-shrink-0 rounded-full bg-blue-600 px-1.5 py-0.5 text-[10px] font-medium text-white">
                          {active.count > 99 ? '99+' : active.count}
                        </span>
                      )}
                      <ChevronDown
                        className={cn(
                          'h-3.5 w-3.5 flex-shrink-0 transition-transform text-gray-500',
                          showCategoryDropdown && 'rotate-180',
                        )}
                      />
                    </button>
                    {showCategoryDropdown && (
                      <div
                        className={cn(
                          'absolute left-0 right-0 top-full z-[200] mt-1 max-h-[min(70vh,32rem)] overflow-y-auto rounded-lg border shadow-xl',
                          isDark
                            ? 'border-gray-700 bg-gray-800'
                            : 'border-gray-200 bg-white',
                        )}
                      >
                        {categoryFilter.options.map((opt) => {
                          const OptIcon = opt.icon;
                          const isCat = categoryFilter.value === opt.key;
                          return (
                            <button
                              key={opt.key}
                              type="button"
                              onClick={() => {
                                categoryFilter.onChange(opt.key);
                                setShowCategoryDropdown(false);
                              }}
                              className={cn(
                                'flex w-full items-center gap-2 px-3 py-2.5 text-left transition-colors',
                                isCat
                                  ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                                  : isDark
                                    ? 'text-gray-300 hover:bg-gray-700'
                                    : 'text-gray-700 hover:bg-gray-100',
                              )}
                            >
                              <OptIcon className="h-4 w-4 shrink-0" />
                              <span className="min-w-0 flex-1 text-sm">{opt.label}</span>
                              {opt.count !== undefined && opt.count > 0 && (
                                <span className="shrink-0 rounded-full bg-blue-600 px-1.5 py-0.5 text-xs font-medium text-white">
                                  {opt.count > 99 ? '99+' : opt.count}
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          ) : null}

          <div className="relative min-w-0 flex-1">
            {isSearching ? (
              <div className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2">
                <div
                  className={cn(
                    'h-3.5 w-3.5 animate-spin rounded-full border-2 border-t-transparent',
                    isDark ? 'border-blue-400' : 'border-blue-600',
                  )}
                />
              </div>
            ) : (
              <Search
                className={cn(
                  'pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2',
                  searchQuery ? 'text-blue-500' : isDark ? 'text-gray-500' : 'text-gray-400',
                )}
              />
            )}
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Search conversations..."
              className={cn(
                'h-8 w-full rounded-lg border bg-white py-1 pl-7 text-[13px] text-gray-900 placeholder-gray-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 touch-manipulation dark:bg-gray-900 dark:text-gray-100',
                isDark ? 'border-gray-600' : 'border-gray-200',
                searchQuery
                  ? 'border-blue-300 pr-7 dark:border-blue-600'
                  : 'pr-2',
              )}
            />
            {searchQuery ? (
              <button
                type="button"
                onClick={() => {
                  onSearchChange('');
                  onClearSearch?.();
                }}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 touch-manipulation"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            ) : null}
          </div>

          <div className="relative flex-shrink-0" ref={sortDropdownRef}>
            <button
              type="button"
              onClick={() => setShowSortDropdown(!showSortDropdown)}
              className={cn(
                'flex h-8 w-8 items-center justify-center rounded-lg border transition-colors',
                showSortDropdown
                  ? 'border-transparent bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-400'
                  : 'border-gray-200 bg-white text-gray-500 hover:bg-gray-100 hover:text-blue-600 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-blue-400',
              )}
              title="Sort conversations"
            >
              <ArrowUpDown className="h-4 w-4" />
            </button>

            {showSortDropdown ? (
              <div
                className={cn(
                  'absolute right-0 top-full z-[200] mt-1 w-48 rounded-lg border shadow-lg',
                  isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white',
                )}
              >
                <div
                  className={cn(
                    'border-b p-2',
                    isDark ? 'border-gray-700' : 'border-gray-200',
                  )}
                >
                  <span
                    className={cn(
                      'text-xs font-medium uppercase text-gray-500 dark:text-gray-400',
                    )}
                  >
                    Sort by
                  </span>
                </div>
                {[
                  { value: 'newest' as const, label: 'Newest first' },
                  { value: 'oldest' as const, label: 'Oldest first' },
                  { value: 'name' as const, label: 'Name (A-Z)' },
                  { value: 'unread' as const, label: 'Unread first' },
                ].map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => {
                      onSortChange(option.value);
                      setShowSortDropdown(false);
                    }}
                    className={cn(
                      'flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700',
                      sortBy === option.value &&
                      'bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400',
                    )}
                  >
                    <span>{option.label}</span>
                    {sortBy === option.value ? <Check className="h-4 w-4 shrink-0" /> : null}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        </div>

        {headerToolbar ? (
          <div
            className={cn(
              useEmailSurface ? 'mt-2 border-t -mx-2 px-2 pt-2' : 'mt-2 border-t px-1 pt-2',
              isDark ? 'border-gray-700' : 'border-gray-200',
            )}
          >
            {headerToolbar}
          </div>
        ) : null}
      </div>
      )}

      {/* Thread List */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex justify-center py-8">
            <BrandedLoadingSpinner mode="compact" size="sm" showSurface={false} showProgressBar={false} />
          </div>
        ) : threads.length > 0 ? (
          <div className="divide-y divide-gray-100 dark:divide-gray-800">
            {threads.map(thread => {
              const hasUnread = Number(thread.unread_count) > 0;
              const isJobThread = isThreadTypeJob(thread.thread_type) || isThreadTypeSchedule(thread.thread_type);
              const isGroupThread = ['group', 'client_group', 'total_operations'].includes(thread.thread_type);
              const isClientRelatedThread = ['client', 'client_group'].includes(thread.thread_type);
              const isTeamThread = thread.thread_type === 'team';
              const participantCount = thread.members?.length || thread.member_count || 0;
              const isSystemThread = thread.thread_type === 'system' || thread.is_system_thread;
              const isOneToOneThread = thread.thread_type === 'direct' || (
                participantCount === 2
                && !isTeamThread
                && !isJobThread
                && !isSystemThread
                && thread.thread_type !== 'total_operations'
              );
              const otherMember = isOneToOneThread
                ? thread.members?.find(m => m.user_id !== currentUserId)
                : null;
              const otherMemberOnline = otherMember ? isMemberOnline(otherMember) : false;
              const otherMemberLastSeen = otherMember ? getMemberLastSeen(otherMember) : null;

              // Determine which avatar to show
              // For explicit job/schedule/client threads, prefer client logo.
              const threadClientLogo = thread.client_logo || thread.system_client_logo || clientLogo;
              const displayAvatar = isTeamThread
                ? null // Team threads use icon with team_color
                : isOneToOneThread
                  ? otherMember?.avatar_url || thread.avatar_url || null
                  : (isJobThread || isClientRelatedThread) && threadClientLogo
                    ? threadClientLogo
                    : thread.avatar_url;
              const displayInitial = isOneToOneThread && otherMember
                ? otherMember.display_name.charAt(0).toUpperCase()
                : thread.thread_name.charAt(0).toUpperCase();

              // Color coding based on thread type (Message Centre pattern)
              const getAvatarGradient = () => {
                if (isTeamThread) return ''; // Team threads use inline style with team_color
                if (isJobThread) return 'bg-gradient-to-br from-blue-500 to-blue-700'; // Blue for jobs
                if (isGroupThread) return ''; // Use moduleColor for groups
                if (isClientRelatedThread) return 'bg-gradient-to-br from-emerald-500 to-emerald-700'; // Green for clients
                if (isOneToOneThread) return 'bg-gradient-to-br from-pink-500 to-rose-600'; // Pink for direct/private
                return ''; // Use moduleColor for default
              };

              // Get typing users for this thread
              const threadTyping = getTypingForThread(thread.thread_id);
              const typingText = threadTyping.length > 0
                ? threadTyping.length === 1
                  ? `${threadTyping[0].userName} is typing...`
                  : `${threadTyping.length} people typing...`
                : null;

              const isSelected = selectedThread?.thread_id === thread.thread_id;
              const avatarRingClass = hasUnread
                ? isDark
                  ? 'ring-2 ring-blue-500/50 ring-offset-2 ring-offset-gray-900'
                  : 'ring-2 ring-blue-200 ring-offset-2 ring-offset-white'
                : '';

              return (
                <button
                  key={thread.thread_id}
                  onClick={() => onSelectThread(thread)}
                  className={cn(
                    useEmailSurface ? 'w-full border-l-4 px-3 py-3 text-left transition-all' : 'w-full border-l-4 px-4 py-3 text-left transition-all',
                    isSelected
                      ? 'border-l-blue-600 bg-blue-50 dark:border-l-blue-600 dark:bg-blue-900/40'
                      : hasUnread
                        ? 'border-l-blue-500 bg-blue-50/70 hover:bg-blue-100/80 dark:border-l-blue-500 dark:bg-blue-950/50 dark:hover:bg-blue-900/40'
                        : 'border-l-transparent hover:bg-gray-50 dark:hover:bg-gray-800/50',
                  )}
                >
                  <div className={cn('flex items-start', useEmailSurface ? 'gap-2.5' : 'gap-3')}>
                    {/* Thread Avatar with unread badge */}
                    <div className="relative flex-shrink-0">
                      {isTeamThread ? (
                        <div
                          className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-medium ${avatarRingClass}`}
                          style={{ backgroundColor: thread.team_color || '#6366f1' }}
                        >
                          <UsersRound className="w-5 h-5" />
                        </div>
                      ) : displayAvatar ? (
                        <img
                          src={displayAvatar}
                          alt={thread.thread_name}
                          className={`w-10 h-10 rounded-full object-cover ${avatarRingClass}`}
                        />
                      ) : (
                        <div
                          className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-medium ${getAvatarGradient()} ${avatarRingClass}`}
                          style={getAvatarGradient() ? undefined : { background: `linear-gradient(135deg, ${moduleColor}, ${moduleColor}dd)` }}
                        >
                          {displayInitial}
                        </div>
                      )}
                      {/* Online status indicator for direct threads */}
                      {isOneToOneThread && otherMember && shouldShowMemberOnline(otherMember) && (
                        <div className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 ${isDark ? 'border-gray-900' : 'border-white'
                          } ${otherMemberOnline ? 'bg-green-500' : 'bg-gray-400'}`} />
                      )}
                      {/* Unread badge on avatar - like WhatsApp */}
                      {hasUnread && (
                        <span className={`absolute -top-1 -right-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold text-white min-w-[18px] text-center shadow-md border-2 ${isDark ? 'bg-blue-500 border-gray-900' : 'bg-blue-600 border-white'
                          }`}>
                          {thread.unread_count}
                        </span>
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        {/* Thread name - truncates to make room */}
                        <h3 className={`text-sm truncate ${hasUnread ? 'font-semibold' : 'font-medium'} ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {thread.thread_name}
                        </h3>
                        {hasUnread && !isSelected && (
                          <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" aria-hidden="true" />
                        )}
                        {/* Team badge */}
                        {isTeamThread && (
                          <span
                            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold text-white flex-shrink-0"
                            style={{ backgroundColor: thread.team_color || moduleColor }}
                          >
                            Team
                          </span>
                        )}
                      </div>

                      {/* Last seen / timestamp row for direct threads */}
                      {isOneToOneThread && otherMember ? (
                        <span className={`text-xs ${hasUnread
                            ? 'font-semibold text-blue-500 dark:text-blue-400'
                            : otherMemberOnline
                              ? 'text-green-500'
                              : isDark ? 'text-gray-400' : 'text-gray-500'
                          }`}>
                          {otherMemberLastSeen || (thread.last_message_at ? formatThreadTime(thread.last_message_at) : '')}
                        </span>
                      ) : thread.last_message_at && (
                        <span className={`text-xs ${hasUnread ? 'font-semibold text-blue-500 dark:text-blue-400' : isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          {formatThreadTime(thread.last_message_at)}
                        </span>
                      )}

                      {/* Show client badge for cross-client threads */}
                      {totalClientsInThreads > 1 && thread.client_name && (
                        <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium mt-0.5 ${isDark
                            ? 'bg-blue-900/50 text-blue-300 border border-blue-700/50'
                            : 'bg-blue-100 text-blue-700 border border-blue-200'
                          }`}>
                          {thread.client_name.length > 15
                            ? thread.client_name.substring(0, 15) + '...'
                            : thread.client_name}
                        </span>
                      )}

                      {/* Thread info row - only show for non-direct threads */}
                      {!isOneToOneThread && (
                        <p className={`text-xs flex items-center gap-1 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                          <Users className="w-3 h-3" />
                          {thread.member_count} members
                          {thread.thread_description && (
                            <>
                              <span className="opacity-50">•</span>
                              <span className="truncate max-w-[150px]">{thread.thread_description}</span>
                            </>
                          )}
                        </p>
                      )}

                      {/* Job-specific details: contact name and location */}
                      {isJobThread && (thread.contact_name || thread.contact_location) && (
                        <p className={`text-xs flex items-center gap-1 mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          {thread.contact_name && (
                            <>
                              <span className="font-medium">{thread.contact_name}</span>
                              {thread.contact_location && <span className="opacity-50">•</span>}
                            </>
                          )}
                          {thread.contact_location && (
                            <span className="truncate max-w-[150px]">{thread.contact_location}</span>
                          )}
                        </p>
                      )}

                      {/* Last message preview or typing indicator */}
                      {typingText ? (
                        <p className={`text-sm italic truncate mt-1 ${isDark ? 'text-green-400' : 'text-green-600'}`}>
                          {typingText}
                        </p>
                      ) : thread.last_message_preview ? (
                        <p className={`text-sm truncate mt-1 ${hasUnread
                            ? isDark ? 'text-gray-100 font-medium' : 'text-gray-900 font-medium'
                            : isDark ? 'text-gray-400' : 'text-gray-600'
                          }`}>
                          <span className={hasUnread ? 'font-semibold' : 'font-medium'}>{thread.last_message_sender}: </span>
                          {thread.last_message_preview}
                        </p>
                      ) : null}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        ) : searchQuery ? (
          <div className={`text-center py-8 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            <Search className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No threads match your search</p>
            <p className="text-sm mt-1">Try a different search term</p>
          </div>
        ) : (
          <div className={`text-center py-8 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            {emptyStateIcon || <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />}
            <p>{emptyStateMessage}</p>
            <p className="text-sm mt-1">Create one to start collaborating</p>
          </div>
        )}
      </div>
    </div>
  );
}
